import jwt_decode from "jwt-decode";
import create from "zustand";
import { persist } from "zustand/middleware";
import { JWT } from "../Models/Auth";
import { DebtType } from "../Models/Credit";
import { RegisterRequired } from "../Models/PredefinedModels";
import { PreviewSummary, ReferenceCodesParam } from "../Models/ReferenceCodes";
import { ReturnData, UserProfile } from "../Models/User";
import { ROUTE_PATHS } from "../Routes/constants";
import { loginAPI, logoutAPI, verifyOtpAPI } from "../Services/AuthService";
import { setApiToken } from "../Services/Axios";
import { FetchRemoteConfig } from "./firebase";
import { otpAccessToken } from "./otpAccessToken";
import { identifyUser } from "./firebaseLogger";
import { identifyMixpanelUser } from "./mixpanel";

interface AuthState {
  user: UserProfile | null;
  accessToken: string | null;
  token: string | null;
  otpData: any | null;
  isFetchedTranslations: boolean;
  isRecurringCompleted: boolean;
  showBanner: boolean;
  setShowBanner: (value: boolean) => void;
  loginUser: (phoneNumber: string, identityNo?: string) => Promise<ReturnData | undefined>;
  verifyUser: (otp: string, token: string) => Promise<any>;
  logout: () => void;
  isLoggedIn: () => boolean;
  fetchRemoteConfig: () => Promise<void>;
  paymentMethod: ReferenceCodesParam | null;
  setPaymentMethod: (paymentMethod: ReferenceCodesParam) => void;
  previewSummary: PreviewSummary | null;
  setPreviewSummary: (data: PreviewSummary) => void;
  setRecurringCompleted: (value: boolean) => void;
  isAllPayment: boolean;  // yeni ekledik
  setIsAllPayment: (value: boolean) => void;  // yeni ekledik
  setOtpData: (data: any) => void;
  clearOtpData: () => void;

  payments: DebtType[] | undefined;
  setPayments: (payments: DebtType[]) => void;
}

const useAuthState = create<AuthState>()(
  persist(
    (set, get) => ({
      user: JSON.parse(localStorage.getItem("user") || "null"),
      accessToken: localStorage.getItem("accessToken"),
      token: null,
      otpData: null,
      isFetchedTranslations: false,
      isRecurringCompleted: false,
      showBanner: false,
      paymentMethod: JSON.parse(
        localStorage.getItem("paymentMethod") || "null"
      ),
      previewSummary: null,
      setPaymentMethod: (paymentMethod: ReferenceCodesParam) =>
        set(() => ({ paymentMethod })),
      setPreviewSummary: (previewSummary: PreviewSummary) =>
        set(() => ({ previewSummary })),
      setRecurringCompleted: (value: boolean) => set({ isRecurringCompleted: value }),
      setShowBanner: (value: boolean) => set({ showBanner: value }),
      loginUser: async (phoneNumber: string, identityNo?: string) => {
        const res = await loginAPI(phoneNumber, identityNo);
        if (res) {
          if (res instanceof RegisterRequired) {
            return { data: res, route: '' }
          }
          set({ token: res.data.token, otpData: res.data });
          const data: ReturnData = { data: res.data, route: ROUTE_PATHS.OTP };
          return data;
        }
      },

      isAllPayment: false,
      setIsAllPayment: (value: boolean) => set(() => ({ isAllPayment: value })),

      verifyUser: async (otp: string, token: string) => {
        const res = await verifyOtpAPI(otp, token);
        const accessToken = otpAccessToken(res);
        if (!accessToken) {
          return false; // error body (9002 "otp code is invalid", any other code) or no response
        }
        localStorage.setItem("accessToken", accessToken);
        const user = jwt_decode<JWT>(accessToken);
        localStorage.setItem("user", JSON.stringify(user));
        set({ accessToken: accessToken });
        set({ user: user });
        set({ isRecurringCompleted: false });
        set({ otpData: null }); // Clear otpData on successful verification
        setApiToken(accessToken);
        identifyMixpanelUser(user);
        identifyUser(user);
        return ROUTE_PATHS.DEFAULT_PAYMENTS;
      },

      logout: async () => {
        const res = await logoutAPI();
        if (res) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("user");
          set({ user: null });
          set({ accessToken: null });
          set({ otpData: null }); // Clear otpData on logout
          window.location.replace("/");
        }
      },

      isLoggedIn: () => !!get().accessToken,

      fetchRemoteConfig: async () => {
        const retryFetchRemoteConfig = async (retryCount: number = 0) => {
          try {
            const result = await FetchRemoteConfig();
            if (result) {
              set({ isFetchedTranslations: true });
              // Check if banner should be shown from remote config
              const showBannerValue = localStorage.getItem("remoteConfig");
              if (showBannerValue) {
                const remoteConfig = JSON.parse(showBannerValue);
                const showBanner = remoteConfig?.show_banner?._value === "true";
                set({ showBanner });
              }
            } else if (retryCount < 3) {
              console.log("couldn't fetch remote config, retrying...");
              await new Promise((resolve) => setTimeout(resolve, 5000));
              await retryFetchRemoteConfig(retryCount + 1);
            }
          } catch (error) {
            console.error("Error fetching remote config:", error);
            if (retryCount < 3) {
              await new Promise((resolve) => setTimeout(resolve, 5000));
              await retryFetchRemoteConfig(retryCount + 1);
            }
          }
        };

        await retryFetchRemoteConfig();
      },

      setOtpData: (data: any) => set(() => ({ otpData: data })),
      clearOtpData: () => set(() => ({ otpData: null })),
      payments: [],
      setPayments: (payments: DebtType[]) => set(() => ({ payments })),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        paymentMethod: state.paymentMethod,
        isRecurringCompleted: state.isRecurringCompleted,
        isAllPayment: state.isAllPayment,
      }),
      getStorage: () => localStorage,
    }
  )
);

export default useAuthState;
